import cn from 'classnames';
import React, { FC, useState } from 'react';

import { Bars } from '@.partials';
import { Filter3, FilterData } from '@.ui';
import { analyticsStoreSelectors } from '@/Store';
import { BestCombinationIngredientsStatistic } from '@/Types/analytics';

import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '../FavoriteHeartChart/constants';
import S from './styles.module.scss';

interface BestCombinationIngredientsChartProps {
    data: BestCombinationIngredientsStatistic;
    mini?: boolean;
}

enum FilterType {
    AGE = 'age',
    SEX = 'sex',
}

export const BestCombinationIngredientsChart: FC<BestCombinationIngredientsChartProps> = ({ data, mini = false }) => {
    const [ageIds, setAgeIds] = useState<number[]>([]);
    const [genderId, setGenderId] = useState<number | undefined>(-1);

    const filterBestCombinationIngredients = analyticsStoreSelectors.use.filterBestCombinationIngredients();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const getFilterValues = (item: FilterData, filterType: FilterType) => {
        if (filterType === FilterType.AGE) {
            const newAgeIds = item.age_id && typeof item.age_id === 'string' ? item.age_id.split(',').map((id) => Number(id)) : undefined;

            if (!newAgeIds) {
                return { ageIds: undefined, genderId };
            }

            setAgeIds(newAgeIds);

            return {
                ageIds: newAgeIds,
                genderId,
            };
        }

        if (filterType === FilterType.SEX) {
            const newGenderId = item.sex_id ? Number(item.sex_id) : undefined;

            setGenderId(newGenderId);

            return { ageIds, genderId: newGenderId };
        }

        return { ageIds: undefined, genderId: undefined };
    };

    const onChangeFilter = (item: FilterData, filterType: FilterType) => {
        filterBestCombinationIngredients(getFilterValues(item, filterType));
    };

    const height = data.dataset.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn(S.ingredients_best_combinations, { [S['ingredients_best_combinations-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Top Ingredients Chosen by Users via AI Wizard</h3>
            {!mini && (
                <div className={S.filtersWrapper}>
                    <Filter3
                        className={S.filter}
                        onChange={(age) => onChangeFilter(age, FilterType.AGE)}
                        fromData={{
                            ages: data.ages,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All ages"
                    >
                        <Filter3.Ages visibleLabel={''} visibleName="All ages" multiple />
                    </Filter3>
                    <Filter3
                        className={S.filter}
                        onChange={(sex) => onChangeFilter(sex, FilterType.SEX)}
                        fromData={{
                            sexes: data.genders,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All genders"
                    >
                        <Filter3.Sexes visibleLabel={''} visibleName="All genders" />
                    </Filter3>
                </div>
            )}

            <Bars data={data.dataset} height={height} isHorizontal axisDataName="Combinations" isHideFractionalTicks mini={mini} />
        </div>
    );
};
